import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Play, Pause, Volume2, Maximize, RotateCw, CheckCircle, Clock, BookOpen, Lock, Smartphone, Monitor, FileText, File, Download, Paperclip, ArrowRight, Award, Sparkles } from 'lucide-react';
import apiService, { videoProgressManager } from '../services/api';
import FileTypeIcon from '../components/FileTypeIcon'

// Simple success sound generator using Web Audio API
const playSuccessSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.setValueAtTime(523, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(659, audioContext.currentTime + 0.1);
    oscillator.frequency.setValueAtTime(784, audioContext.currentTime + 0.2);
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
  } catch (error) {
    console.log('Audio not supported:', error);
  }
};

// Completion Modal Component
const CompletionModal = ({ 
    isOpen, 
    chapterTitle,
    moduleName,
    hasNextChapter, 
    nextChapterTitle, 
    hasNextModule,
    nextModuleTitle,
    isModuleComplete,
    onContinue, 
    onClose,
    progress = 0 
  }) => {
    const [showContent, setShowContent] = useState(false);
    const [animationComplete, setAnimationComplete] = useState(false);

    useEffect(() => {
      if (isOpen) {
        playSuccessSound();
        setTimeout(() => setShowContent(true), 300);
        setTimeout(() => setAnimationComplete(true), 800);
      } else {
        setShowContent(false);
        setAnimationComplete(false);
      }
    }, [isOpen]);

    if (!isOpen) return null;

    const remainingSeconds = Math.max(0, Math.ceil(7 - progress));
    const progressPercentage = Math.min(100, (progress / 7) * 100);

    return (
      <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 transition-all duration-500 ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}>
        <div className={`relative bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 max-w-lg w-full transform transition-all duration-700 ${
          showContent ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}>
          <div className="text-center mb-4 sm:mb-6">
            <div className={`mx-auto w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mb-4 sm:mb-6 transform transition-all duration-1000 ${
              animationComplete ? 'scale-100 rotate-0' : 'scale-0 rotate-180'
            }`}>
              {isModuleComplete ? (
                <Award className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white" />
              ) : (
                <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white" />
              )}
            </div>
            
            <div className="relative">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className={`absolute w-2 h-2 bg-yellow-400 rounded-full animate-ping transition-all duration-1000 ${
                    animationComplete ? 'opacity-100' : 'opacity-0'
                  }`}
                  style={{
                    left: `${20 + i * 15}%`,
                    top: `${-10 + (i % 2) * 20}px`,
                    animationDelay: `${i * 200}ms`,
                  }}
                />
              ))}
            </div>
          </div>

          <div className={`text-center transform transition-all duration-700 delay-300 ${
            showContent ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
          }`}>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
              ¡Felicitaciones! 🎉
            </h2>
            
            {isModuleComplete ? (
              <>
                <p className="text-gray-700 mb-2 text-base sm:text-lg">
                  Has completado exitosamente el módulo
                </p>
                
                <p className="text-lg sm:text-xl font-semibold text-green-600 mb-4 sm:mb-6 px-2">
                  "{moduleName}"
                </p>
                
                <div className="text-center p-3 sm:p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl mb-4 sm:mb-6">
                  <Award className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500 mx-auto mb-2" />
                  <p className="text-green-800 font-medium text-sm sm:text-base">¡Has completado todos los capítulos!</p>
                  <p className="text-xs sm:text-sm text-green-600">¡Excelente trabajo!</p>
                </div>
              </>
            ) : (
              <>
                <p className="text-gray-700 mb-2 text-base sm:text-lg">
                  Has completado exitosamente
                </p>
                
                <p className="text-lg sm:text-xl font-semibold text-blue-600 mb-4 sm:mb-6 px-2">
                  "{chapterTitle}"
                </p>
              </>
            )}

            {progress > 0 && (hasNextChapter || hasNextModule) && (
              <div className="mb-4 sm:mb-6">
                <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600 mb-2">
                  <span>Continuando automáticamente...</span>
                  <span className="font-semibold">{remainingSeconds}s</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              </div>
            )}

            <div className="space-y-2 sm:space-y-3">
              {hasNextChapter && (
                <button
                  onClick={() => onContinue('next-chapter')}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base font-medium hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:scale-105 flex items-center justify-center space-x-2"
                >
                  <Play className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="truncate">Continuar al Siguiente Capítulo</span>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                </button>
              )}

              {hasNextModule && (
                <button
                  onClick={() => onContinue('next-module')}
                  className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base font-medium hover:from-green-700 hover:to-green-800 transition-all duration-200 transform hover:scale-105 flex items-center justify-center space-x-2"
                >
                  <Award className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="truncate">Continuar al Siguiente Módulo</span>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                </button>
              )}

              {!hasNextChapter && !hasNextModule && (
                <div className="text-center p-3 sm:p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg sm:rounded-xl">
                  <Award className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500 mx-auto mb-2" />
                  <p className="text-green-800 font-medium text-sm sm:text-base">¡Has completado todos los módulos!</p>
                  <p className="text-xs sm:text-sm text-green-600">¡Excelente trabajo!</p>
                </div>
              )}

              <button
                onClick={onClose}
                className="w-full bg-gray-100 text-gray-700 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base font-medium hover:bg-gray-200 transition-all duration-200"
              >
                Quedarme Aquí
              </button>
            </div>

            {hasNextChapter && nextChapterTitle && (
              <div className="mt-3 sm:mt-4 p-2.5 sm:p-3 bg-blue-50 rounded-lg">
                <p className="text-xs sm:text-sm text-blue-700">
                  <strong>Próximo capítulo:</strong> {nextChapterTitle}
                </p>
              </div>
            )}

            {!hasNextChapter && hasNextModule && nextModuleTitle && (
              <div className="mt-3 sm:mt-4 p-2.5 sm:p-3 bg-green-50 rounded-lg">
                <p className="text-xs sm:text-sm text-green-700">
                  <strong>Próximo módulo:</strong> {nextModuleTitle}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

const EnhancedChapterView = ({ selectedModule, selectedChapter, chapters, onNavigate, onBack, allModules = [] }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  
  // Video state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ watch_time: 0, completed: false });
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const [completionError, setCompletionError] = useState(null);

  // Animation state
  const [contentLoaded, setContentLoaded] = useState(false);

  // Materials state
  const [materials, setMaterials] = useState([]);
  const [materialsLoading, setMaterialsLoading] = useState(false);

  // Responsive video state
  const [videoAspectRatio, setVideoAspectRatio] = useState(16/9);
  const [isVerticalVideo, setIsVerticalVideo] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [orientation, setOrientation] = useState('landscape');
  const [optimalViewMode, setOptimalViewMode] = useState('standard');

  // Auto-save progress
  const [lastSaveTime, setLastSaveTime] = useState(0);
  const saveInterval = 10000;

  // Completion Modal state
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [autoProgressTimer, setAutoProgressTimer] = useState(null);
  const [autoProgressCount, setAutoProgressCount] = useState(0);
  const [completionModalData, setCompletionModalData] = useState({
    chapterTitle: '',
    moduleName: '',
    hasNextChapter: false,
    nextChapterTitle: '',
    hasNextModule: false,
    nextModuleTitle: '',
    isModuleComplete: false
  });

  // Trigger animations after mount
  useEffect(() => {
    setTimeout(() => setContentLoaded(true), 100);
  }, []);

  // Auto-progression logic
  const getNextChapter = () => {
    const currentIndex = chapters.findIndex(ch => ch.id === selectedChapter.id);
    return currentIndex < chapters.length - 1 ? chapters[currentIndex + 1] : null;
  };

  const getNextModule = () => {
    if (!allModules || allModules.length === 0) {
      console.log('❌ No modules available for next module detection');
      return null;
    }
    
    console.log('🔍 Current module ID:', selectedModule.id);
    console.log('🔍 All modules:', allModules.map(m => ({ id: m.id, title: m.title })));
    
    const currentModuleIndex = allModules.findIndex(mod => mod.id === selectedModule.id);
    console.log('🔍 Current module index:', currentModuleIndex);
    
    if (currentModuleIndex >= 0 && currentModuleIndex < allModules.length - 1) {
      const nextModule = allModules[currentModuleIndex + 1];
      console.log('✅ Next module found:', nextModule.title);
      return nextModule;
    }
    
    console.log('❌ No next module available');
    return null;
  };

  const isLastChapterInModule = () => {
    const currentIndex = chapters.findIndex(ch => ch.id === selectedChapter.id);
    return currentIndex === chapters.length - 1;
  };

  const handleCompletionModalAction = (action) => {
    if (autoProgressTimer) {
      clearTimeout(autoProgressTimer);
      setAutoProgressTimer(null);
    }
    setAutoProgressCount(0);
    setShowCompletionModal(false);

    if (action === 'next-chapter') {
      const nextChapter = getNextChapter();
      if (nextChapter) {
        onNavigate('chapter', {
          selectedModule,
          selectedChapter: nextChapter,
          chapters
        });
      }
    } else if (action === 'next-module') {
      const nextModule = getNextModule();
      if (nextModule) {
        onNavigate('module', { selectedModule: nextModule });
      }
    }
  };

  const startAutoProgressTimer = () => {
    let count = 0;
    const timer = setInterval(() => {
      count += 0.1;
      setAutoProgressCount(count);
      
      if (count >= 7) {
        clearInterval(timer);
        const nextChapter = getNextChapter();
        const nextModule = getNextModule();
        
        console.log('🚀 Auto-progression triggered after 7 seconds:');
        console.log('  - Next Chapter:', nextChapter?.title || 'None');
        console.log('  - Next Module:', nextModule?.title || 'None');
        
        setAutoProgressTimer(null);
        setAutoProgressCount(0);
        setShowCompletionModal(false);
        
        if (nextChapter) {
          console.log('✅ Navigating to next chapter');
          onNavigate('chapter', {
            selectedModule,
            selectedChapter: nextChapter,
            chapters
          });
        } else if (nextModule) {
          console.log('✅ Navigating to next module');
          onNavigate('module', { selectedModule: nextModule });
        } else {
          console.log('❌ No next content available - staying on current page');
        }
      }
    }, 100);
    
    setAutoProgressTimer(timer);
  };

  // Backup: Handle video ended event
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
  
    const handleVideoEnded = () => {
      console.log('🎬 Video "ended" event fired!');
      console.log('   State check:');
      console.log('   - Completed:', progress.completed);
      console.log('   - Marking:', isMarkingComplete);
      console.log('   - Modal shown:', showCompletionModal);
      
      // ✅ FIXED: Show modal even if already completed (but modal not shown yet)
      if (!showCompletionModal && !isMarkingComplete) {
        if (progress.completed) {
          console.log('⚠️ Already marked complete, but modal not shown - showing modal now');
          // Just show the modal without marking complete again
          const nextChapter = getNextChapter();
          const nextModule = getNextModule();
          const isModuleComplete = isLastChapterInModule();
          
          setCompletionModalData({
            chapterTitle: selectedChapter.title,
            moduleName: selectedModule.title,
            hasNextChapter: !!nextChapter,
            nextChapterTitle: nextChapter?.title || '',
            hasNextModule: !!nextModule,
            nextModuleTitle: nextModule?.title || '',
            isModuleComplete: isModuleComplete
          });
          
          setShowCompletionModal(true);
          
          if (nextChapter || nextModule) {
            startAutoProgressTimer();
          }
        } else {
          console.log('✅ Triggering completion from video end event');
          handleMarkComplete();
        }
      } else {
        console.log('⏭️ Skipping (modal already shown or in progress)');
      }
    };
    
    video.addEventListener('ended', handleVideoEnded);
    
    return () => {
      video.removeEventListener('ended', handleVideoEnded);
    };
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress.completed, isMarkingComplete, showCompletionModal]);


  // Device and orientation detection
  useEffect(() => {
    const checkDevice = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      const currentOrientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
      setOrientation(currentOrientation);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    window.addEventListener('orientationchange', () => {
      setTimeout(checkDevice, 100);
    });

    return () => {
      window.removeEventListener('resize', checkDevice);
      window.removeEventListener('orientationchange', checkDevice);
    };
  }, []);

  useEffect(() => {
    console.log('\n🔄 Materials state changed:');
    console.log('Current materials:', materials);
    console.log('Length:', materials.length);
    
    if (materials.length > 0) {
      console.log('\n📊 Material IDs in state:');
      materials.forEach((material, index) => {
        console.log(`  [${index}] ID: ${material.id} (${typeof material.id}) - Title: ${material.title}`);
      });
      
      const ids = materials.map(m => m.id);
      const uniqueIds = [...new Set(ids)];
      if (ids.length !== uniqueIds.length) {
        console.error('❌ DUPLICATE IDs FOUND!', ids);
      }
      
      const materialWithId5 = materials.find(m => m.id === 5 || m.id === '5');
      if (materialWithId5) {
        console.error('❌ FOUND MATERIAL WITH ID 5!', materialWithId5);
      }
    }
  }, [materials]);

  useEffect(() => {
    if (videoAspectRatio && !loading) {
      let viewMode = 'standard';
      
      const isVertical = videoAspectRatio < 1;
      setIsVerticalVideo(isVertical);
      
      if (isMobile) {
        if (orientation === 'portrait') {
          viewMode = isVertical ? 'mobile-vertical-optimized' : 'mobile-horizontal-contained';
        } else {
          viewMode = isVertical ? 'mobile-landscape-vertical' : 'mobile-landscape-optimized';
        }
      } else {
        if (isVertical) {
          viewMode = 'desktop-vertical-centered';
        } else {
          viewMode = 'desktop-horizontal-standard';
        }
      }
      
      setOptimalViewMode(viewMode);
      console.log(`📱 Video aspect ratio: ${videoAspectRatio.toFixed(2)}, View mode: ${viewMode}`);
    }
  }, [videoAspectRatio, isMobile, orientation, loading]);

  useEffect(() => {
    // ✅ Guard clause: Only run if selectedChapter exists
    if (!selectedChapter || !selectedChapter.id) {
      console.warn('⚠️ selectedChapter not ready yet');
      return;
    }

    console.log('📌 ChapterView mounted/updated for chapter:', selectedChapter.id);
    
    // Scroll to top when chapter changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Load chapter data
    loadChapterProgress();
    initializeVideo();
    fetchChapterMaterials();

    // Cleanup function
    return () => {
      console.log('🧹 Cleaning up chapter view');
      
      // Save progress if video was playing
      if (selectedChapter?.id && currentTime > 0 && duration > 0) {
        saveProgressToServer(currentTime, duration);
      }
      
      // Stop progress tracking
      if (selectedChapter?.id) {
        videoProgressManager.stopTracking(selectedChapter.id);
      }
      
      // Clear auto-progress timer
      if (autoProgressTimer) {
        clearTimeout(autoProgressTimer);
        setAutoProgressTimer(null);
      }
    };
    
    // ✅ Only depend on selectedChapter.id - other functions are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChapter?.id]);

  const fetchChapterMaterials = async () => {
    try {
      setMaterialsLoading(true);
      console.log('🔍 Fetching materials for chapter:', selectedChapter.id);
      
      const materialsData = await apiService.getChapterMaterials(selectedChapter.id);
      console.log('📋 RAW API Response:', materialsData);
      
      if (Array.isArray(materialsData)) {
        materialsData.forEach((material, index) => {
          console.log(`\n=== Material ${index} ===`);
          console.log('Full object:', material);
          console.log('ID:', material.id, '(type:', typeof material.id, ')');
          console.log('Chapter ID:', material.chapter_id);
          console.log('Title:', material.title);
        });
      }
      
      setMaterials(materialsData || []);
      
    } catch (error) {
      console.error('Failed to fetch materials:', error);
      setMaterials([]);
    } finally {
      setMaterialsLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const downloadMaterial = async (material) => {
    console.log('\n🚨 DOWNLOAD FUNCTION CALLED');
    console.log('📦 Material object received:', material);
    console.log('📦 Material.id:', material.id, '(type:', typeof material.id, ')');
    
    if (material.id === 5 || material.id === '5') {
      console.error('❌ TRYING TO DOWNLOAD ID 5! This should not happen!');
      alert('DEBUG: Trying to download material ID 5, but this ID does not exist in database!');
      return;
    }
    
    try {
      const downloadUrl = `${apiService.baseURL}/chapters/${selectedChapter.id}/materials/${material.id}/download`;
      console.log('🔗 Download URL:', downloadUrl);
      
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${apiService.token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('📡 Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Download error response:', errorText);
        
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.available_materials) {
            console.log('✅ Available material IDs from server:', errorData.available_materials);
            alert(`Material ID ${material.id} not found. Available materials: ${errorData.available_materials.join(', ')}`);
          }
        } catch (parseError) {
          console.log('Could not parse error as JSON');
        }
        
        throw new Error(`Download failed: ${response.status} - ${response.statusText}`);
      }

      const blob = await response.blob();
      console.log('📦 Blob received, size:', blob.size);
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const filename = material.original_filename || material.original_name || material.file_name || material.filename || material.title || 'download';
      link.download = filename;
      
      console.log('💾 Downloading as:', filename);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Download completed successfully');
      
    } catch (error) {
      console.error('❌ Download error:', error);
      alert(`Error al descargar el archivo: ${error.message}`);
    }
  };

  const loadChapterProgress = async () => {
    try {
      const progressData = await videoProgressManager.loadProgress(selectedChapter.id);
      setProgress(progressData);
      console.log(`📥 Loaded progress: ${progressData.watch_time}s, completed: ${progressData.completed}`);
    } catch (err) {
      console.error('Failed to load progress:', err);
    }
  };

  const initializeVideo = () => {
    setLoading(true);
    setError(null);
    
    console.log('🎬 Initializing video for chapter:', selectedChapter.id);
    console.log('📋 Chapter data:', {
      id: selectedChapter.id,
      title: selectedChapter.title,
      video_url: selectedChapter.video_url,
      video_filename: selectedChapter.video_filename,
      b2_file_id: selectedChapter.b2_file_id,
      b2_file_name: selectedChapter.b2_file_name
    });
    
    // Check if video_url exists
    if (!selectedChapter.video_url) {
      console.error('❌ No video_url found for this chapter!');
      setError('No hay video disponible para este capítulo');
      setLoading(false);
      return;
    }
    
    if (videoRef.current) {
      videoRef.current.load();
    }
  };

  const handleVideoLoad = () => {
    const video = videoRef.current;
    if (video && !isNaN(video.duration) && video.duration > 0) {
      console.log(`📹 Video loaded - Duration: ${video.duration}s`);
      setDuration(video.duration);
      
      const aspectRatio = video.videoWidth / video.videoHeight;
      setVideoAspectRatio(aspectRatio);
      console.log(`📐 Video dimensions: ${video.videoWidth}x${video.videoHeight}, Aspect ratio: ${aspectRatio.toFixed(2)}`);
      
      setLoading(false);
      
      videoProgressManager.startTracking(selectedChapter.id, video.duration);
      
      if (progress.watch_time > 0 && !progress.completed && progress.watch_time < video.duration) {
        video.currentTime = progress.watch_time;
        console.log(`⏭️ Resumed from ${progress.watch_time}s`);
      }
    } else {
      console.warn('⚠️ Video loaded but duration is invalid:', video?.duration);
      setTimeout(() => {
        if (video && !isNaN(video.duration) && video.duration > 0) {
          handleVideoLoad();
        }
      }, 1000);
    }
  };

  const handleVideoError = (e) => {
    console.error('❌ Video loading error:', {
      error: e,
      video_url: selectedChapter.video_url,
      constructed_url: selectedChapter.video_url ? apiService.getVideoUrl(selectedChapter.video_url) : 'N/A',
      error_code: videoRef.current?.error?.code,
      error_message: videoRef.current?.error?.message
    });
    
    // Provide more specific error messages
    const errorCode = videoRef.current?.error?.code;
    let errorMessage = 'Error al cargar el video';
    
    switch(errorCode) {
      case 1: // MEDIA_ERR_ABORTED
        errorMessage = 'Carga del video cancelada';
        break;
      case 2: // MEDIA_ERR_NETWORK
        errorMessage = 'Error de red al cargar el video. Verifica tu conexión a internet.';
        break;
      case 3: // MEDIA_ERR_DECODE
        errorMessage = 'Error al decodificar el video. El archivo puede estar corrupto.';
        break;
      case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
        errorMessage = 'Formato de video no soportado o URL inválida. Contacta al administrador.';
        break;
      default:
        errorMessage = 'Error al cargar el video';
    }
    
    setError(errorMessage);
    setLoading(false);
  };


  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (video && !isNaN(video.currentTime) && !isNaN(video.duration)) {
      const currentTime = video.currentTime;
      const duration = video.duration;
      
      setCurrentTime(currentTime);
      setDuration(duration);

      const progressPercent = (currentTime / duration) * 100;
      const timeRemaining = duration - currentTime;
      
      // Log progress at 10% intervals for debugging
      const currentProgressFloor = Math.floor(progressPercent / 10) * 10;
      const lastProgressFloor = Math.floor(((currentTime - 0.5) / duration) * 100 / 10) * 10;
      if (currentProgressFloor !== lastProgressFloor && currentProgressFloor > 0) {
        console.log(`📊 Video progress: ${currentProgressFloor}% (${Math.round(timeRemaining)}s remaining)`);
      }
      
      // ✅ IMPROVED: Trigger 5 seconds before end
      const isNearEnd = (
        timeRemaining <= 3 ||                         // Change 5 → 10
        progressPercent >= 3 ||
        (duration > 0 && currentTime >= duration - 3) // Change 5 → 10
      );
      
      // Only trigger if we haven't shown the modal yet
      const shouldTriggerCompletion = (
          isNearEnd &&
          !showCompletionModal &&
          !isMarkingComplete &&
          duration > 3 &&                               // Change 5 → 10
          currentTime > 3
      );
      
      if (shouldTriggerCompletion) {
        console.log('🎯 TRIGGERING COMPLETION MODAL (5 seconds before end)!');
        console.log(`   Progress: ${Math.round(progressPercent)}%`);
        console.log(`   Time remaining: ${Math.round(timeRemaining)}s`);
        console.log(`   Current time: ${Math.round(currentTime)}s / ${Math.round(duration)}s`);
        handleMarkComplete();
      }

      // Auto-save progress every 10 seconds
      const now = Date.now();
      if (now - lastSaveTime >= saveInterval) {
        saveProgressToServer(currentTime, duration);
        setLastSaveTime(now);
      }
    }
  };





  const saveProgressToServer = async (currentTime, videoDuration) => {
    // ✅ Guard: Don't save if chapter doesn't exist
    if (!selectedChapter?.id) {
      console.warn('⚠️ Cannot save progress: no chapter selected');
      return;
    }
    
    try {
      if (currentTime >= 0 && videoDuration > 0 && !isNaN(currentTime) && !isNaN(videoDuration)) {
        await apiService.updateVideoProgress(selectedChapter.id, {
          currentTime: currentTime,
          duration: videoDuration
        });
        console.log(`💾 Progress saved: ${Math.round(currentTime)}s / ${Math.round(videoDuration)}s`);
      } else {
        console.warn('⚠️ Skipping progress save - invalid data:', { currentTime, videoDuration });
      }
    } catch (err) {
      console.error('Failed to save progress:', err);
    }
  };

  const handleMarkComplete = async () => {
    if (isMarkingComplete) return;
    
    setIsMarkingComplete(true);
    setCompletionError(null);
    
    try {
      if (currentTime > 0) {
        await saveProgressToServer(currentTime, duration);
      }
      
      const result = await videoProgressManager.markComplete(selectedChapter.id);
      setProgress(prev => ({ ...prev, completed: true }));
      console.log('✅ Chapter marked as complete successfully');
      
      const nextChapter = getNextChapter();
      const nextModule = getNextModule();
      const isModuleComplete = isLastChapterInModule();
      
      console.log('🔍 Completion Analysis:');
      console.log('  - Next Chapter:', nextChapter?.title || 'None');
      console.log('  - Next Module:', nextModule?.title || 'None');
      console.log('  - Is Module Complete:', isModuleComplete);
      console.log('  - Current Module:', selectedModule.title);
      
      setCompletionModalData({
        chapterTitle: selectedChapter.title,
        moduleName: selectedModule.title,
        hasNextChapter: !!nextChapter,
        nextChapterTitle: nextChapter?.title || '',
        hasNextModule: !!nextModule,
        nextModuleTitle: nextModule?.title || '',
        isModuleComplete: isModuleComplete
      });
      
      setShowCompletionModal(true);
      
      if (nextChapter || nextModule) {
        console.log('🚀 Starting auto-progress timer...');
        startAutoProgressTimer();
      } else {
        console.log('📋 No next content - showing completion without auto-progress');
      }
      
    } catch (err) {
      console.error('❌ Failed to mark chapter complete:', err);
      setCompletionError('Error al marcar el capítulo como completado');
    } finally {
      setIsMarkingComplete(false);
    }
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (video) {
      if (video.paused) {
        video.play();
        setIsPlaying(true);
      } else {
        video.pause();
        setIsPlaying(false);
      }
    }
  };

  const handleSeek = (e) => {
    const video = videoRef.current;
    if (video && duration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const newTime = (clickX / rect.width) * duration;
      video.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (video) {
      if (!document.fullscreenElement) {
        video.requestFullscreen().then(() => {
          setIsFullscreen(true);
        });
      } else {
        document.exitFullscreen().then(() => {
          setIsFullscreen(false);
        });
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = () => {
    return duration > 0 ? (currentTime / duration) * 100 : 0;
  };

  const getCurrentChapterIndex = () => {
    return chapters.findIndex(ch => ch.id === selectedChapter.id);
  };

  const handleNextChapter = () => {
    const nextChapter = getNextChapter();
    if (nextChapter) {
      onNavigate('chapter', { 
        selectedModule, 
        selectedChapter: nextChapter, 
        chapters 
      });
    }
  };

  const getVideoContainerStyle = () => {
    const baseStyle = "relative bg-black flex items-center justify-center";
    
    switch (optimalViewMode) {
      case 'mobile-vertical-optimized':
        return `${baseStyle} h-[70vh] w-full`;
      case 'mobile-horizontal-contained':
        return `${baseStyle} h-[40vh] w-full`;
      case 'mobile-landscape-vertical':
        return `${baseStyle} h-[60vh] w-auto mx-auto`;
      case 'mobile-landscape-optimized':
        return `${baseStyle} h-[80vh] w-full`;
      case 'desktop-vertical-centered':
        return `${baseStyle} h-[70vh] w-auto max-w-md mx-auto`;
      case 'desktop-horizontal-standard':
        return `${baseStyle} h-[60vh] w-full`;
      default:
        return `${baseStyle} h-[60vh] w-full`;
    }
  };

  const getVideoStyle = () => {
    const baseStyle = "w-full h-full object-contain";
    
    if (optimalViewMode === 'mobile-landscape-vertical') {
      return `${baseStyle} max-w-[50vh]`;
    }
    
    if (optimalViewMode === 'desktop-vertical-centered') {
      return `${baseStyle} max-w-full`;
    }
    
    return baseStyle;
  };

  // Animation styles
  const animationStyles = `
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes slideInLeft {
      from { opacity: 0; transform: translateX(-20px); }
      to { opacity: 1; transform: translateX(0); }
    }
    @keyframes scaleIn {
      from { opacity: 0; transform: scale(0.95); }
      to { opacity: 1; transform: scale(1); }
    }
    .animate-fadeInUp { animation: fadeInUp 0.6s ease-out forwards; }
    .animate-fadeIn { animation: fadeIn 0.5s ease-out forwards; }
    .animate-slideInLeft { animation: slideInLeft 0.5s ease-out forwards; }
    .animate-scaleIn { animation: scaleIn 0.4s ease-out forwards; }
    .stagger-1 { animation-delay: 0.1s; }
    .stagger-2 { animation-delay: 0.2s; }
    .stagger-3 { animation-delay: 0.3s; }
    .stagger-4 { animation-delay: 0.4s; }
  `;

  if (!selectedChapter) {
    return <div>No chapter selected</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{animationStyles}</style>
      
      <CompletionModal
        isOpen={showCompletionModal}
        chapterTitle={completionModalData.chapterTitle}
        moduleName={completionModalData.moduleName}
        hasNextChapter={completionModalData.hasNextChapter}
        nextChapterTitle={completionModalData.nextChapterTitle}
        hasNextModule={completionModalData.hasNextModule}
        nextModuleTitle={completionModalData.nextModuleTitle}
        isModuleComplete={completionModalData.isModuleComplete}
        onContinue={handleCompletionModalAction}
        onClose={() => {
          clearTimeout(autoProgressTimer);
          setAutoProgressTimer(null);
          setAutoProgressCount(0);
          setShowCompletionModal(false);
        }}
        progress={autoProgressCount}
      />

      {/* Header */}
      <header className={`bg-white shadow-sm border-b sticky top-0 z-40 ${contentLoaded ? 'animate-fadeIn' : 'opacity-0'}`}>
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3 md:py-4">
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={onBack}
              className="flex items-center space-x-1.5 sm:space-x-2 text-gray-600 hover:text-blue-600 transition-all duration-300 hover:scale-105"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-xs sm:text-sm md:text-base font-medium">Volver</span>
            </button>
            
            <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4">
              <div className="hidden sm:flex items-center space-x-2 text-xs text-gray-500">
                {isVerticalVideo ? (
                  <Smartphone className="w-3 h-3 sm:w-4 sm:h-4" />
                ) : (
                  <Monitor className="w-3 h-3 sm:w-4 sm:h-4" />
                )}
                <span className="hidden md:inline">{isVerticalVideo ? 'Vertical' : 'Horizontal'}</span>
              </div>
              
              {progress.completed && (
                <div className="flex items-center space-x-1 sm:space-x-2 text-green-600">
                  <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" />
                  <span className="text-xs sm:text-sm font-medium">Completado</span>
                </div>
              )}
              
              <div className="text-xs sm:text-sm text-gray-500 font-medium">
                {getCurrentChapterIndex() + 1}/{chapters.length}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4 md:py-6 lg:py-8">
        {/* Chapter Info */}
        <div className={`bg-white rounded-lg md:rounded-xl shadow-sm border p-3 sm:p-4 md:p-6 mb-3 sm:mb-4 md:mb-6 ${contentLoaded ? 'animate-fadeInUp' : 'opacity-0'}`}>
          <div className="flex items-start space-x-2 sm:space-x-3 md:space-x-4">
            <div className="bg-blue-100 p-2 sm:p-2.5 md:p-3 rounded-lg flex-shrink-0">
              <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-1 sm:mb-2 line-clamp-2">
                {selectedChapter.title}
              </h1>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                <div className="flex items-center space-x-1">
                  <span className="truncate">Módulo: {selectedModule.title}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Video Player */}
        <div className={`bg-white rounded-lg md:rounded-xl shadow-sm border overflow-hidden mb-3 sm:mb-4 md:mb-6 ${contentLoaded ? 'animate-scaleIn stagger-1' : 'opacity-0'}`}>
          <div ref={containerRef} className={getVideoContainerStyle()}>
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900 animate-fadeIn">
                <div className="text-white text-center px-4">
                  <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 border-b-2 border-white mx-auto mb-3"></div>
                  <p className="text-sm sm:text-base">Cargando video...</p>
                </div>
              </div>
            )}

            {error && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900 animate-fadeIn">
                <div className="text-white text-center p-4">
                  <p className="text-red-400 mb-4 text-sm sm:text-base">{error}</p>
                  <button
                    onClick={initializeVideo}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all duration-300 text-sm sm:text-base transform hover:scale-105"
                  >
                    <RotateCw className="w-4 h-4 inline mr-2" />
                    Reintentar
                  </button>
                </div>
              </div>
            )}

            <video
              ref={videoRef}
              className={getVideoStyle()}
              onLoadedData={handleVideoLoad}
              onError={handleVideoError}
              onTimeUpdate={handleTimeUpdate}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              controls={false}
              playsInline
            >
              <source 
                src={apiService.getVideoUrl(selectedChapter.video_url)} 
                type="video/mp4" 
              />
              Tu navegador no soporta la reproducción de video.
            </video>

            {/* Custom Controls */}
            {!loading && !error && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 sm:p-3 md:p-4">
                <div className="mb-2 sm:mb-3 md:mb-4">
                  <div 
                    className="w-full bg-gray-600 rounded-full h-1 sm:h-1.5 md:h-2 cursor-pointer transition-all duration-200 hover:h-2 md:hover:h-3"
                    onClick={handleSeek}
                  >
                    <div 
                      className="bg-blue-500 h-full rounded-full transition-all duration-300"
                      style={{ width: `${getProgressPercentage()}%` }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4">
                    <button
                      onClick={togglePlay}
                      className="text-white hover:text-blue-400 transition-all duration-300 transform hover:scale-110"
                    >
                      {isPlaying ? (
                        <Pause className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8" />
                      ) : (
                        <Play className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8" />
                      )}
                    </button>

                    <div className="text-white text-xs sm:text-sm font-medium">
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4">
                    <div className="hidden sm:flex items-center space-x-2">
                      <Volume2 className="w-4 h-4 md:w-5 md:h-5 text-white" />
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={volume}
                        onChange={(e) => {
                          const newVolume = parseFloat(e.target.value);
                          setVolume(newVolume);
                          if (videoRef.current) {
                            videoRef.current.volume = newVolume;
                          }
                        }}
                        className="w-16 md:w-20"
                      />
                    </div>

                    <button
                      onClick={toggleFullscreen}
                      className="text-white hover:text-blue-400 transition-all duration-300 transform hover:scale-110"
                    >
                      <Maximize className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {!loading && videoAspectRatio && (
            <div className="bg-gray-50 px-3 sm:px-4 py-2 border-t text-xs text-gray-600">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <span className="flex items-center space-x-1">
                    {isVerticalVideo ? '📱 Vertical' : '🖥️ Horizontal'}
                  </span>
                  <span className="text-gray-500 hidden sm:inline">
                    Ratio: {videoAspectRatio.toFixed(2)}
                  </span>
                </div>
                <div className="hidden md:block text-gray-500">
                  Modo: {optimalViewMode.replace(/-/g, ' ')}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Materials Section */}
        {materials.length > 0 && (
          <div className={`bg-white rounded-lg md:rounded-xl shadow-sm border p-3 sm:p-4 md:p-6 mb-3 sm:mb-4 md:mb-6 ${contentLoaded ? 'animate-fadeInUp stagger-2' : 'opacity-0'}`}>
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Material de Apoyo</h3>
              {materialsLoading && (
                <div className="text-xs sm:text-sm text-gray-500">Cargando...</div>
              )}
            </div>

            <div className="space-y-2 sm:space-y-3">
              {materials.map((material, arrayIndex) => (
                <div
                  key={`material-${material.id || arrayIndex}`}
                  className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-md gap-2 sm:gap-3"
                >
                  <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                    <FileTypeIcon 
                      filename={material.original_filename || material.original_name || material.file_name || material.filename || material.title || 'unknown.file'} 
                      size="w-8 h-8 sm:w-10 sm:h-10" 
                    />
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium text-gray-900 text-sm sm:text-base truncate">
                        {material.title || material.original_name}
                      </h4>
                      <div className="flex flex-wrap items-center gap-x-2 text-xs sm:text-sm text-gray-500">
                        {material.file_size && (
                          <span>{formatFileSize(material.file_size)}</span>
                        )}
                        {material.created_at && material.file_size && (
                          <span className="hidden sm:inline">•</span>
                        )}
                        {material.created_at && (
                          <span className="hidden sm:inline">
                            {new Date(material.created_at).toLocaleDateString('es-ES')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      console.log(`\n🖱️ Download button clicked!`);
                      console.log('🖱️ Array index:', arrayIndex);
                      console.log('🖱️ Material object:', material);
                      console.log('🖱️ Material ID:', material.id);
                      downloadMaterial(material);
                    }}
                    className="flex items-center space-x-1.5 sm:space-x-2 bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 text-xs sm:text-sm flex-shrink-0"
                  >
                    <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Descargar</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}



        {/* Progress & Completion */}
        <div className={`bg-white rounded-lg md:rounded-xl shadow-sm border p-3 sm:p-4 md:p-6 mb-3 sm:mb-4 md:mb-6 ${contentLoaded ? 'animate-fadeInUp stagger-3' : 'opacity-0'}`}>
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900">Progreso del Capítulo</h3>
            <div className="text-xs sm:text-sm text-gray-500 font-medium">
              {Math.round(getProgressPercentage())}% completado
            </div>
          </div>

          <div className="mb-3 sm:mb-4">
            <div className="w-full bg-gray-200 rounded-full h-2 sm:h-2.5 md:h-3">
              <div 
                className={`h-full rounded-full transition-all duration-500 ${
                  progress.completed ? 'bg-green-500' : 'bg-blue-500'
                }`}
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div className="flex items-center space-x-2">
              {progress.completed ? (
                <>
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                  <span className="text-green-700 font-medium text-xs sm:text-sm md:text-base">¡Capítulo completado!</span>
                </>
              ) : (
                <>
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                  <span className="text-gray-600 text-xs sm:text-sm md:text-base">
                    Tiempo: {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </>
              )}
            </div>

            {!progress.completed && getProgressPercentage() > 50 && (
              <button
                onClick={handleMarkComplete}
                disabled={isMarkingComplete}
                className="bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center space-x-2 text-xs sm:text-sm md:text-base transition-all duration-300 transform hover:scale-105"
              >
                {isMarkingComplete ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Marcando...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span className="whitespace-nowrap">Marcar Completado</span>
                  </>
                )}
              </button>
            )}
          </div>

          {completionError && (
            <div className="mt-3 sm:mt-4 p-2.5 sm:p-3 bg-red-50 border border-red-200 rounded-lg animate-fadeIn">
              <p className="text-red-800 text-xs sm:text-sm">{completionError}</p>
              <button
                onClick={handleMarkComplete}
                className="mt-2 text-red-600 hover:text-red-800 text-xs sm:text-sm underline"
              >
                Reintentar
              </button>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className={`bg-white rounded-lg md:rounded-xl shadow-sm border p-3 sm:p-4 md:p-6 ${contentLoaded ? 'animate-fadeInUp stagger-4' : 'opacity-0'}`}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
            <div>
              <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">Navegación</h3>
              <p className="text-xs sm:text-sm md:text-base text-gray-600">
                Capítulo {getCurrentChapterIndex() + 1} de {chapters.length} en {selectedModule.title}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {getCurrentChapterIndex() > 0 && (
                <button
                  onClick={() => {
                    const prevChapter = chapters[getCurrentChapterIndex() - 1];
                    onNavigate('chapter', { 
                      selectedModule, 
                      selectedChapter: prevChapter, 
                      chapters 
                    });
                  }}
                  className="bg-gray-100 text-gray-700 px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2 text-xs sm:text-sm md:text-base transition-all duration-300 transform hover:scale-105"
                >
                  <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>Anterior</span>
                </button>
              )}

              {getNextChapter() && (
                <button
                  onClick={handleNextChapter}
                  className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 text-xs sm:text-sm md:text-base transition-all duration-300 transform hover:scale-105"
                >
                  <span>Siguiente</span>
                  <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 rotate-180" />
                </button>
              )}

              <button
                onClick={onBack}
                className="bg-gray-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-700 text-xs sm:text-sm md:text-base transition-all duration-300 transform hover:scale-105"
              >
                Volver
              </button>
            </div>
          </div>

          {/* Chapter List Preview */}
          <div className="mt-4 sm:mt-6 border-t pt-4 sm:pt-6">
            <h4 className="font-medium text-gray-900 mb-2 sm:mb-3 text-xs sm:text-sm md:text-base">Otros capítulos:</h4>
            <div className="grid gap-2">
              {chapters.map((chapter, index) => (
                <button
                  key={chapter.id}
                  onClick={() => {
                    if (chapter.id !== selectedChapter.id) {
                      onNavigate('chapter', { 
                        selectedModule, 
                        selectedChapter: chapter, 
                        chapters 
                      });
                    }
                  }}
                  className={`flex items-center space-x-2 sm:space-x-3 p-2.5 sm:p-3 rounded-lg text-left transition-all duration-300 transform hover:scale-[1.02] ${
                    chapter.id === selectedChapter.id
                      ? 'bg-blue-50 border border-blue-200'
                      : 'hover:bg-gray-50 hover:shadow-sm'
                  }`}
                >
                  <div className={`w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium flex-shrink-0 ${
                    chapter.completed 
                      ? 'bg-green-100 text-green-600'
                      : chapter.id === selectedChapter.id
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {chapter.completed ? (
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                    ) : (
                      index + 1
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium text-xs sm:text-sm md:text-base truncate ${
                      chapter.id === selectedChapter.id ? 'text-blue-900' : 'text-gray-900'
                    }`}>
                      {chapter.title}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500">{chapter.duration}</p>
                  </div>
                  
                  {chapter.id === selectedChapter.id && (
                    <div className="text-blue-600 text-xs sm:text-sm font-medium flex-shrink-0">
                      Actual
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedChapterView;